import React from "react";

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700;900&family=DM+Sans:wght@300;400;500;600&family=IBM+Plex+Sans+Arabic:wght@300;400;500;600;700&family=Amiri:wght@400;700&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    :root {
      --gold: #C9A84C;
      --gold-light: #E8C97A;
      --gold-dark: #A07830;
      --deep: #0D1B2A;
      --deep2: #162032;
      --cream: #F9F4EC;
      --text: #2C2C2C;
      --text-muted: #6B6B6B;
      --white: #FFFFFF;
      --border: rgba(201,168,76,0.25);
      --font-body: 'DM Sans', sans-serif;
      --font-heading: 'Playfair Display', serif;
      --font-ui: 'DM Sans', sans-serif;
    }

    html { scroll-behavior: smooth; }

    img {
      max-width: 100%;
    }

    body {
      font-family: var(--font-body);
      background: var(--cream);
      color: var(--text);
      overflow-x: hidden;
    }

    h1,h2,h3,h4 { font-family: var(--font-heading); }

    html[lang="ar"] {
      --font-body: 'IBM Plex Sans Arabic', sans-serif;
      --font-heading: 'Amiri', serif;
      --font-ui: 'IBM Plex Sans Arabic', sans-serif;
    }

    html[lang="ar"] body {
      letter-spacing: 0;
      text-rendering: optimizeLegibility;
      line-height: 1.85;
    }

    html[lang="ar"] input,
    html[lang="ar"] textarea,
    html[lang="ar"] select,
    html[lang="ar"] button,
    html[lang="ar"] .section-tag,
    html[lang="ar"] .section-subtitle {
      font-family: var(--font-ui);
    }

    html[lang="ar"] .section-tag {
      letter-spacing: 1px;
      text-transform: none;
    }

    html[lang="ar"] .btn-gold,
    html[lang="ar"] .btn-outline {
      letter-spacing: 0;
    }

    html[lang="ar"] h1,
    html[lang="ar"] h2,
    html[lang="ar"] h3,
    html[lang="ar"] h4,
    html[lang="ar"] .section-title {
      line-height: 1.45;
    }

    /* Scrollbar */
    ::-webkit-scrollbar { width: 6px; }
    ::-webkit-scrollbar-track { background: var(--cream); }
    ::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 3px; }

    /* Animations */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(30px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes fadeIn {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    @keyframes shimmer {
      0%   { background-position: -200% center; }
      100% { background-position: 200% center; }
    }
    @keyframes float {
      0%,100% { transform: translateY(0px); }
      50%      { transform: translateY(-12px); }
    }
    @keyframes pulse-ring {
      0%   { transform: scale(0.8); opacity: 1; }
      100% { transform: scale(2); opacity: 0; }
    }

    .fade-up   { animation: fadeUp 0.7s ease forwards; }
    .fade-in   { animation: fadeIn 0.5s ease forwards; }

    /* Gold shimmer text */
    .gold-text {
      background: linear-gradient(90deg, var(--gold-dark), var(--gold-light), var(--gold), var(--gold-dark));
      background-size: 200% auto;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      animation: shimmer 4s linear infinite;
    }

    /* Buttons */
    .btn-gold {
      background: linear-gradient(135deg, var(--gold), var(--gold-dark));
      color: var(--white);
      border: none;
      padding: 14px 32px;
      font-family: var(--font-ui);
      font-weight: 600;
      font-size: 15px;
      letter-spacing: 0.5px;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.3s ease;
      text-decoration: none;
      display: inline-block;
    }
    .btn-gold:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(201,168,76,0.4);
    }

    .btn-outline {
      background: transparent;
      color: var(--gold);
      border: 1.5px solid var(--gold);
      padding: 13px 32px;
      font-family: var(--font-ui);
      font-weight: 600;
      font-size: 15px;
      cursor: pointer;
      border-radius: 4px;
      transition: all 0.3s ease;
      text-decoration: none;
      display: inline-block;
    }
    .btn-outline:hover {
      background: var(--gold);
      color: var(--white);
    }

    /* Cards */
    .card {
      background: var(--white);
      border-radius: 12px;
      overflow: hidden;
      box-shadow: 0 4px 20px rgba(0,0,0,0.08);
      transition: all 0.35s ease;
    }
    .card:hover {
      transform: translateY(-6px);
      box-shadow: 0 16px 40px rgba(0,0,0,0.14);
    }

    /* Section titles */
    .section-tag {
      color: var(--gold);
      font-family: var(--font-ui);
      font-weight: 600;
      font-size: 13px;
      letter-spacing: 3px;
      text-transform: uppercase;
      margin-bottom: 12px;
      display: block;
    }
    .section-title {
      font-size: clamp(28px, 4vw, 44px);
      color: var(--deep);
      line-height: 1.2;
      margin-bottom: 16px;
    }
    .section-subtitle {
      color: var(--text-muted);
      font-size: 16px;
      line-height: 1.7;
      max-width: 560px;
    }

    /* Divider */
    .gold-divider {
      width: 60px;
      height: 3px;
      background: linear-gradient(90deg, var(--gold), var(--gold-light));
      margin: 16px 0 24px;
      border-radius: 2px;
    }

    /* Responsive */
    @media (max-width: 768px) {
      .hide-mobile { display: none !important; }
    }
    @media (min-width: 769px) {
      .hide-desktop { display: none !important; }
    }
  `}</style>
);

export default GlobalStyles;
